import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Search, Monitor, MapPin, Signal } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { PlayerPublish } from "./lib/types"
import { publishPlayers } from "./lib/actions"
import { toast } from "sonner";

interface MssPublishAllDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  players?: PlayerPublish[];
}

export function MssPublishAllDialog({
  open,
  onOpenChange,
  players = [],
}: MssPublishAllDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);

  const filteredPlayers = players.filter(
    (player) =>
      player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      player.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId)
        ? prev.filter((id) => id !== playerId)
        : [...prev, playerId]
    );
  };

  const toggleAllPlayers = () => {
    if (selectedPlayers.length === filteredPlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(filteredPlayers.map((p) => p.id));
    }
  };

  const handlePublish = async () => {
    try {
      await publishPlayers(selectedPlayers);
      toast.success(`Published to ${selectedPlayers.length} player${selectedPlayers.length !== 1 ? "s" : ""}`);
    } catch {
      toast.error("Failed to publish. Please try again.");
    }
    setSelectedPlayers([]);
    setSearchQuery("");
    onOpenChange(false);
  };

  const handleCancel = () => {
    setSelectedPlayers([]);
    setSearchQuery("");
    onOpenChange(false);
  };

  const getStatusColor = (status: PlayerPublish["status"]) => {
    switch (status) {
      case "online":
        return "bg-green-500";
      case "offline":
        return "bg-zinc-400";
      case "warning":
        return "bg-yellow-500";
      default:
        return "bg-zinc-400";
    }
  };

  const getStatusText = (status: PlayerPublish["status"]) => {
    switch (status) {
      case "online":
        return "Online";
      case "offline":
        return "Offline";
      case "warning":
        return "Warning";
      default:
        return "Unknown";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-150">
        <DialogHeader>
          <DialogTitle>Publish to Players</DialogTitle>
          <DialogDescription>
            Select the players you want to publish to.
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar */}
        <div className="py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Select All Checkbox */}
        {filteredPlayers.length > 0 && (
          <div className="flex items-center gap-2 pb-2">
            <Checkbox
              checked={
                selectedPlayers.length === filteredPlayers.length &&
                filteredPlayers.length > 0
              }
              onCheckedChange={toggleAllPlayers}
              id="select-all"
            />
            <label
              htmlFor="select-all"
              className="text-sm font-medium cursor-pointer"
            >
              Select All ({filteredPlayers.length} players)
            </label>
          </div>
        )}

        {/* Players List */}
        <ScrollArea className="h-100 pr-4">
          <div className="space-y-2">
            {filteredPlayers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Monitor className="w-12 h-12 text-zinc-300 mb-3" />
                <p className="text-sm text-zinc-600">
                  {searchQuery
                    ? "No players found matching your search"
                    : "No players available"}
                </p>
              </div>
            ) : (
              filteredPlayers.map((player) => (
                <div
                  key={player.id}
                  className={`flex items-start gap-3 p-4 rounded-lg border transition-colors cursor-pointer ${
                    selectedPlayers.includes(player.id)
                      ? "border-blue-500 bg-blue-50"
                      : "border-zinc-200 hover:bg-zinc-50"
                  }`}
                  onClick={() => togglePlayerSelection(player.id)}
                >
                  <Checkbox
                    checked={selectedPlayers.includes(player.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Monitor className="w-4 h-4 text-zinc-500 shrink-0" />
                        <h4 className="text-sm font-medium text-zinc-900 truncate">
                          {player.name}
                        </h4>
                      </div>
                      <Badge
                        variant={
                          player.status === "online" ? "default" : "secondary"
                        }
                        className="shrink-0"
                      >
                        <div
                          className={`w-2 h-2 rounded-full mr-1.5 ${getStatusColor(player.status)}`}
                        />
                        {getStatusText(player.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-600">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-3 h-3" />
                        <span>{player.location}</span>
                      </div>
                      {player.deviceType && (
                        <div className="flex items-center gap-1.5">
                          <Signal className="w-3 h-3" />
                          <span>{player.deviceType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Selected Count */}
        <div className="pt-2 border-t">
          <p className={`text-sm text-zinc-600 ${selectedPlayers.length === 0 ? "invisible" : ""}`}>
            {selectedPlayers.length} player{selectedPlayers.length !== 1 ? "s" : ""} selected
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={selectedPlayers.length === 0}
          >
            Publish to {selectedPlayers.length || "0"} Player
            {selectedPlayers.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
